// src/tracking/events.js
const num = (v) => {
  const n = Number(String(v).replace(/\s/g,'').replace(',','.'));
  return Number.isFinite(n) ? n : 0;
};

// wybór stabilnego ID (sku > variantId > productId)
const pickId = ({ product, variant, fallbackId }) =>
  variant?.sku || product?.sku || String(variant?.id ?? product?.id ?? fallbackId ?? '');

const gaItem = ({ product, variant, price, quantity=1, currency='PLN' }) => ({
  item_id: pickId({ product, variant }),
  item_name: product?.name,
  item_variant: variant?.type?.name,
  price: num(price),
  quantity: Number(quantity) || 1,
  currency
});

export const track = {
  // już masz page_view w main.js; helper jeśli kiedyś potrzebny:
  pageView(to) {
    const page_location = window.location.href;
    const page_path = to?.fullPath || (window.location.pathname + window.location.search);
    const page_title = document.title || 'birdstool.pl';
    window.gtag?.('event', 'page_view', { page_location, page_path, page_title });
    window.fbq?.('track', 'PageView');
  },

  // LISTA (opcjonalnie: jeśli chcesz logować listy)
  viewItemList({ listId, listName, products=[], currency='PLN' }) {
    window.gtag?.('event', 'view_item_list', {
      item_list_id: listId, item_list_name: listName,
      items: products.map(p => ({
        item_id: p.sku || String(p.id),
        item_name: p.name,
        price: num(p.price_pln ?? p.price ?? 0),
        quantity: 1,
        currency
      }))
    });
    window.fbq?.('track', 'ViewContent', {
      content_type: 'product_group',
      content_ids: products.map(p => p.sku || String(p.id)),
      value: 0,
      currency
    });
  },

  // SZCZEGÓŁY PRODUKTU
  viewItem({ product, variant, price, currency='PLN' }) {
    window.gtag?.('event', 'view_item', {
      currency, value: num(price),
      items: [ gaItem({ product, variant, price, currency }) ]
    });
    window.fbq?.('track', 'ViewContent', {
      content_type: 'product',
      content_ids: [ pickId({ product, variant }) ],
      content_name: product?.name,
      value: num(price),
      currency
    });
  },

  // DODANIE DO KOSZYKA
  addToCart({ product, variant, price, quantity=1, currency='PLN' }) {
    const id = pickId({ product, variant });
    const value = num(price) * (Number(quantity) || 1);
    window.gtag?.('event', 'add_to_cart', {
      currency, value,
      items: [ gaItem({ product, variant, price, quantity, currency }) ]
    });
    window.fbq?.('track', 'AddToCart', {
      content_type: 'product',
      content_ids: [ id ],
      contents: [{ id, quantity: Number(quantity) || 1 }],
      value, currency
    });
  },

  // ROZPOCZĘCIE ZAKUPU
  beginCheckout({ items=[], currency='PLN', shipping=0, valueOverride }) {
    const value = valueOverride ?? (items.reduce((s,i)=> s + num(i.price)*Number(i.quantity||1), 0) + num(shipping));
    window.gtag?.('event', 'begin_checkout', {
      currency, value,
      items: items.map(i => gaItem({
        product: { id: i.product_id ?? i.id, name: i.product_name ?? i.name },
        variant: i.variant, price: i.price, quantity: i.quantity, currency
      }))
    });
    window.fbq?.('track', 'InitiateCheckout', {
      currency, value,
      contents: items.map(i => ({
        id: i.sku || String(i.product_id ?? i.id),
        quantity: Number(i.quantity)||1
      })),
      content_type: 'product'
    });
  },

  // DODANIE METODY PŁATNOŚCI (opcjonalnie)
  addPaymentInfo({ items=[], currency='PLN', paymentType='other' }) {
    window.gtag?.('event', 'add_payment_info', {
      currency, payment_type: paymentType,
      items: items.map(i => gaItem({
        product: { id: i.product_id ?? i.id, name: i.product_name ?? i.name },
        variant: i.variant, price: i.price, quantity: i.quantity, currency
      }))
    });
    window.fbq?.('track', 'AddPaymentInfo', {
      currency,
      value: items.reduce((s,i)=> s + num(i.price)*Number(i.quantity||1), 0),
      contents: items.map(i => ({
        id: i.sku || String(i.product_id ?? i.id),
        quantity: Number(i.quantity)||1
      })),
      content_type: 'product'
    });
  },

  // LEAD (formularze)
  lead({ formId='contact', status='submitted' } = {}) {
    window.gtag?.('event', 'generate_lead', { method: 'contact_form', form_id: formId, status });
    window.fbq?.('track', 'Lead', { form_id: formId, status, content_name: 'Contact form' });
  },

  // ZAKUP (UWAGA: przy Stripe/PayPal rekomendowane przez backend/Webhook)
  purchase({ transaction_id, items=[], currency='PLN', shipping=0, tax=0 }) {
    const value = items.reduce((s,i)=> s + num(i.price)*Number(i.quantity||1), 0) + num(shipping) + num(tax);
    window.gtag?.('event', 'purchase', {
      transaction_id, currency, value, tax: num(tax), shipping: num(shipping),
      items: items.map(i => gaItem({
        product: { id: i.product_id ?? i.id, name: i.product_name ?? i.name },
        variant: i.variant, price: i.price, quantity: i.quantity, currency
      }))
    });
    window.fbq?.('track', 'Purchase', {
      currency, value,
      contents: items.map(i => ({
        id: i.sku || String(i.product_id ?? i.id),
        quantity: Number(i.quantity)||1
      })),
      content_type: 'product'
    });
  },

  // klik tel/mail (opcjonalnie)
  contactClick(type, value) {
    window.gtag?.('event', 'select_content', { content_type: `contact_${type}`, item_id: value });
    window.fbq?.('trackCustom', 'ContactClick', { type, value });
  }
};
